<template>
  <div class="inventory-visibility d-flex flex-column align-start ga-1" @click.stop>
    <!-- Line 1: one combined badge (status, hide reason, Needs-review icon); details in the tooltip -->
    <div class="d-flex align-center">
      <!-- System-hidden rows (D8): non-actionable status with an accessible name -->
      <v-tooltip
        v-if="systemHidden"
        :text="hiddenDetails"
        location="top"
        open-on-hover
        open-on-focus
        open-on-click
      >
        <template #activator="{ props: tooltipProps }">
          <span
            v-bind="tooltipProps"
            class="visibility-badge visibility-badge--hidden visibility-badge--static"
            role="img"
            :aria-label="
              assessedCause
                ? 'Website visibility: Hidden by the system'
                : 'Website visibility: Hidden — No reliable reason recorded'
            "
            tabindex="0"
            data-testid="visibility-status-system-hidden"
          >
            <v-icon icon="mdi-eye-off-outline" size="14" />
            <span class="visibility-badge__label text-truncate" aria-hidden="true">{{ badgeLabel }}</span>
            <v-icon v-if="stale" icon="mdi-alert-circle-outline" color="error" size="14" aria-hidden="true" />
          </span>
        </template>
      </v-tooltip>

      <!-- Ordinary rows, disabled: the wrapper receives hover, focus and tap for the tooltip [C14.F1] -->
      <v-tooltip v-else-if="actionDisabled" :text="badgeTooltip" location="top" open-on-click>
        <template #activator="{ props: tooltipProps }">
          <span
            v-bind="tooltipProps"
            class="visibility-badge-wrap"
            tabindex="0"
            data-testid="visibility-badge-wrap"
          >
            <button
              type="button"
              class="visibility-badge"
              :class="[
                isVisible ? 'visibility-badge--visible' : 'visibility-badge--hidden',
                { 'visibility-badge--pending': pendingAction === 'toggle' },
              ]"
              disabled
              :aria-busy="toggling ? 'true' : undefined"
              :aria-label="badgeAriaLabel"
              data-testid="visibility-badge"
              @click="emit('request-toggle', !isVisible)"
            >
              <v-progress-circular
                v-if="pendingAction === 'toggle'"
                indeterminate
                size="14"
                width="2"
                aria-hidden="true"
                data-testid="visibility-badge-loading"
              />
              <v-icon v-else :icon="isVisible ? 'mdi-eye-outline' : 'mdi-eye-off-outline'" size="14" />
              <span class="visibility-badge__label text-truncate" aria-hidden="true">{{ badgeLabel }}</span>
              <span v-if="stale" data-testid="visibility-needs-review" aria-hidden="true" class="d-inline-flex">
                <v-icon icon="mdi-alert-circle-outline" color="error" size="14" />
              </span>
            </button>
          </span>
        </template>
      </v-tooltip>

      <!-- Ordinary rows, enabled: the button itself is the tooltip activator -->
      <v-tooltip v-else :text="badgeTooltip" location="top">
        <template #activator="{ props: tooltipProps }">
          <button
            v-bind="tooltipProps"
            type="button"
            class="visibility-badge"
            :class="isVisible ? 'visibility-badge--visible' : 'visibility-badge--hidden'"
            :aria-busy="toggling ? 'true' : undefined"
            :aria-label="badgeAriaLabel"
            data-testid="visibility-badge"
            @click="emit('request-toggle', !isVisible)"
          >
            <v-icon :icon="isVisible ? 'mdi-eye-outline' : 'mdi-eye-off-outline'" size="14" />
            <span class="visibility-badge__label text-truncate" aria-hidden="true">{{ badgeLabel }}</span>
            <span v-if="stale" data-testid="visibility-needs-review" aria-hidden="true" class="d-inline-flex">
              <v-icon icon="mdi-alert-circle-outline" color="error" size="14" />
            </span>
          </button>
        </template>
      </v-tooltip>
    </div>

    <!-- Auto / Manual segmented control (reflects the stored mode only) -->
    <div
      v-if="vehicle.websiteVisibilityMode"
      class="visibility-mode"
      role="group"
      aria-label="Visibility control"
    >
      <v-tooltip location="top" max-width="280">
        <template #activator="{ props: tip }">
          <span
            class="visibility-mode__item"
            v-bind="modeTooltipActivator(tip)"
            data-testid="visibility-mode-auto-tip"
          >
            <v-btn
              size="x-small"
              :variant="isManual ? 'text' : 'tonal'"
              :color="isManual ? undefined : 'primary'"
              rounded="0"
              height="18"
              :loading="pendingAction === 'auto'"
              :aria-pressed="!isManual ? 'true' : 'false'"
              :aria-describedby="`${helpId}-auto`"
              class="visibility-mode__btn text-none"
              data-testid="visibility-mode-auto"
              @click.stop="emit('request-auto')"
            >
              Auto
            </v-btn>
          </span>
        </template>
        <div class="font-weight-medium">{{ VISIBILITY_MODE_HELP.AUTO.title }}</div>
        <div>{{ VISIBILITY_MODE_HELP.AUTO.text }}</div>
      </v-tooltip>
      <v-tooltip location="top" max-width="280">
        <template #activator="{ props: tip }">
          <span
            class="visibility-mode__item"
            v-bind="modeTooltipActivator(tip)"
            data-testid="visibility-mode-manual-tip"
          >
            <v-btn
              size="x-small"
              :variant="isManual ? 'tonal' : 'text'"
              :color="isManual ? 'primary' : undefined"
              rounded="0"
              height="18"
              :loading="pendingAction === 'manual'"
              :disabled="actionDisabled"
              :aria-pressed="isManual ? 'true' : 'false'"
              :aria-describedby="`${helpId}-manual`"
              class="visibility-mode__btn text-none"
              data-testid="visibility-mode-manual"
              @click.stop="emit('request-manual')"
            >
              Manual
            </v-btn>
          </span>
        </template>
        <div class="font-weight-medium">{{ VISIBILITY_MODE_HELP.MANUAL.title }}</div>
        <div>{{ VISIBILITY_MODE_HELP.MANUAL.text }}</div>
      </v-tooltip>
    </div>
    <template v-if="vehicle.websiteVisibilityMode">
      <span :id="`${helpId}-auto`" class="d-sr-only">
        {{ VISIBILITY_MODE_HELP.AUTO.title }}. {{ VISIBILITY_MODE_HELP.AUTO.text }}
      </span>
      <span :id="`${helpId}-manual`" class="d-sr-only">
        {{ VISIBILITY_MODE_HELP.MANUAL.title }}. {{ VISIBILITY_MODE_HELP.MANUAL.text }}
      </span>
    </template>
  </div>
</template>

<script lang="ts" setup>
import { computed, useId } from 'vue'
import {
  INVENTORY_STATE,
  WEBSITE_VISIBILITY,
  WEBSITE_VISIBILITY_MODE,
} from '@jazel-auto/ise-contracts/dist/inventory.js'
import type { InventoryVehicle } from '@/services/inventory'
import { describeHiddenDetails, hiddenBadgeCause, VISIBILITY_MODE_HELP } from '@/utils/inventoryTableDisplay'

// Presentational line-3 visibility controls (R4.1-R4.4). State, mutations and the confirmation
// dialog live in Inventory.vue; this component only renders and emits intents. The root has
// @click.stop so no control here triggers the row click handler (R4.6).
const props = defineProps<{
  vehicle: InventoryVehicle
  toggling: boolean
  // Which visibility action is pending for this row (R15); only its control shows a loader.
  pendingAction?: 'toggle' | 'manual' | 'auto' | null
  systemHidden: boolean
  explanation: string
  daysHidden: number | null
  stale: boolean
}>()

const emit = defineEmits<{
  (e: 'request-toggle', nextVisible: boolean): void
  (e: 'request-manual'): void
  (e: 'request-auto'): void
}>()

const helpId = useId()

// The wrapper keeps Vuetify's activator ref and hover handlers (so hover works on a disabled Manual
// button, which has pointer-events: none). Native focus does not bubble, so the tooltip's focus
// handlers are attached as focusin / focusout; they still require :focus-visible on the event target.
// The wrapper's aria-describedby is dropped because each button carries its own description.
const modeTooltipActivator = (tip: Record<string, unknown>) => {
  const { onFocus, onBlur, 'aria-describedby': _describedBy, ...rest } = tip
  return {
    ...rest,
    onFocusin: onFocus as ((event: FocusEvent) => void) | undefined,
    onFocusout: onBlur as ((event: FocusEvent) => void) | undefined,
  }
}

const isArchived = computed(() => props.vehicle.inventoryState === INVENTORY_STATE.ARCHIVED)
const isVisible = computed(
  () => !isArchived.value && props.vehicle.websiteVisibility !== WEBSITE_VISIBILITY.HIDDEN,
)
const isManual = computed(() => props.vehicle.websiteVisibilityMode === WEBSITE_VISIBILITY_MODE.MANUAL)

// Same rule as the former switch: toggling, REMOVED or ARCHIVED rows have no actions.
const actionDisabled = computed(
  () => props.toggling || props.vehicle.membershipStatus === 'REMOVED' || isArchived.value,
)

const isHidden = computed(() => props.vehicle.websiteVisibility === WEBSITE_VISIBILITY.HIDDEN)
// The cause comes only from the evidence assessment (`explanation`), never the raw stored reason (R16).
const assessedCause = computed(() => (isHidden.value ? hiddenBadgeCause(props.explanation) : null))
const badgeReason = computed(() => assessedCause.value)
// Archived rows without a stored HIDDEN reason read plain "Hidden" (IC10).
const badgeLabel = computed(() =>
  isVisible.value ? 'Visible' : badgeReason.value ? `Hidden · ${badgeReason.value}` : 'Hidden',
)
const hiddenDetails = computed(() =>
  isHidden.value
    ? describeHiddenDetails({
        explanation: props.explanation,
        reasonLabel: assessedCause.value,
        daysHidden: props.daysHidden,
        stale: props.stale,
      })
    : props.explanation,
)

const badgeTooltip = computed(() => {
  if (isArchived.value) return 'Hidden — vehicle is archived'
  const details = isHidden.value ? hiddenDetails.value : props.explanation.replace(/\.$/, '')
  return `${details} — click to ${isVisible.value ? 'hide' : 'show'}`
})

const badgeAriaLabel = computed(() => {
  if (isArchived.value) return 'Website visibility: Hidden — vehicle is archived'
  return `Website visibility: ${badgeLabel.value}.${props.stale ? ' Needs review.' : ''} ${
    actionDisabled.value ? '' : isVisible.value ? 'Activate to hide.' : 'Activate to show.'
  }`.trim()
})
</script>

<style scoped>
.visibility-badge {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 0 8px;
  border-radius: 9999px;
  border: 1px solid transparent;
  font-size: 0.75rem;
  line-height: 1.125rem;
  font-weight: 500;
  background: transparent;
  color: inherit;
}

.visibility-badge:not(.visibility-badge--static):not(:disabled) {
  cursor: pointer;
}

.visibility-badge:focus-visible {
  outline: 2px solid rgb(var(--v-theme-primary));
  outline-offset: 2px;
}

.visibility-badge:disabled {
  opacity: 0.6;
  cursor: default;
  pointer-events: none;
}

/* A pending Show/Hide keeps the badge and its loader at full emphasis (R15). */
.visibility-badge--pending:disabled {
  opacity: 1;
}

.visibility-badge--visible {
  background-color: rgba(var(--v-theme-success), 0.12);
  color: rgb(var(--v-theme-success));
  border-color: rgba(var(--v-theme-success), 0.4);
}

.visibility-badge--hidden {
  background-color: rgba(var(--v-theme-warning), 0.12);
  color: rgb(var(--v-theme-warning));
  border-color: rgba(var(--v-theme-warning), 0.4);
}

.visibility-badge__label {
  display: inline-block;
  max-width: 150px;
}

.visibility-badge-wrap {
  display: inline-flex;
  align-items: center;
}

.flex-none {
  flex: none;
}

.visibility-mode {
  display: inline-flex;
  border: 1px solid rgba(var(--v-theme-on-surface), 0.2);
  border-radius: 4px;
  overflow: hidden;
}

.visibility-mode__item {
  display: inline-flex;
}

.visibility-mode__item + .visibility-mode__item {
  border-left: 1px solid rgba(var(--v-theme-on-surface), 0.2);
}

.visibility-mode__btn {
  min-width: 0;
  padding: 0 8px;
}

/* The Manual button stays disabled while its own action is pending; its loader is not faded (R15). */
.visibility-mode__btn.v-btn--loading.v-btn--disabled {
  opacity: 1;
}
</style>
